import { useState } from "react";
import * as React from "react";
import { Delete, Edit, Redo, Share as ShareIcon } from "@mui/icons-material";
import { ListItemIcon, Menu, MenuItem, Typography } from "@mui/material";

import { usePermissionCheck } from "fond/hooks/usePermissionCheck";
import Share from "fond/share/Share";
import { Folder } from "fond/types";
import { EntityMenuSource } from "fond/types/analytics";
import { Actions, permissionCheck } from "fond/utils/permissions";
import { replaceAllSpecialCharacters } from "fond/utils/strings";

import DeleteCityPlannerModal from "../CityPlanner/DeleteCityPlannerModal";
import EditCityPlannerModal from "../CityPlanner/EditCityPlannerModal";
import MoveFolderDialog from "../Folders/MoveFolderDialog";

import StarMenuItem from "./StarMenuItem";
import { CommonItemMenuProps } from "./types";

interface CityPlannerRowMenuProps extends CommonItemMenuProps {
  /**
   * The market lens folder the menu actions relate to
   */
  cityPlannerFolder: Folder;
  open?: boolean;
  source: EntityMenuSource;
}

type FolderModal = "edit" | "delete" | "move" | "share";

const CityPlannerRowMenu: React.FC<CityPlannerRowMenuProps> = ({
  cityPlannerFolder,
  onMenuClose,
  anchorEl,
  open = true,
  source,
}: CityPlannerRowMenuProps) => {
  const [showModal, setShowModal] = useState<FolderModal>();
  const canEditCityPlannerFolder = usePermissionCheck(Actions.CITY_PLANNER_EDIT, cityPlannerFolder?.Permission.Level);
  const canDeleteCityPlannerFolder = usePermissionCheck(Actions.FOLDER_DELETE, cityPlannerFolder?.Permission.Level);
  const canMoveCityPlannerFolder = usePermissionCheck(Actions.CITY_PLANNER_MOVE, cityPlannerFolder?.Permission.Level);

  if (!cityPlannerFolder) return null;

  /**
   * Generic Callback function that updates any of the modal open states and closes the menu
   */
  const openModal = (modalType: FolderModal) => (event: React.MouseEvent<EventTarget>) => {
    onMenuClose(event);
    setShowModal(modalType);
  };

  const closeModal = () => {
    setShowModal(undefined);
  };

  return (
    <Menu
      data-testid={`project-list-item-menu-${replaceAllSpecialCharacters(cityPlannerFolder.Name, "")}`}
      anchorEl={anchorEl}
      keepMounted
      open={!!anchorEl && open}
      onClose={onMenuClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
    >
      <MenuItem
        aria-label="Move Market Lens project"
        onClick={openModal("move")}
        data-testid="move-folder-button"
        disabled={!canMoveCityPlannerFolder}
      >
        <ListItemIcon>
          <Redo />
        </ListItemIcon>
        <Typography variant="inherit">Move</Typography>
      </MenuItem>

      <MenuItem
        aria-label="Edit Market Lens project"
        onClick={openModal("edit")}
        data-testid="edit-folder-button"
        disabled={!canEditCityPlannerFolder}
      >
        <ListItemIcon>
          <Edit />
        </ListItemIcon>
        <Typography variant="inherit">Edit</Typography>
      </MenuItem>

      <MenuItem
        aria-label="Share Market Lens project"
        onClick={openModal("share")}
        data-testid="share-folder-button"
        disabled={!permissionCheck(cityPlannerFolder.Permission.Level, Actions.FOLDER_SHARE_VIEW)}
      >
        <ListItemIcon>
          <ShareIcon />
        </ListItemIcon>
        <Typography variant="inherit">Share</Typography>
      </MenuItem>

      <MenuItem
        aria-label="Delete Market Lens project"
        onClick={openModal("delete")}
        data-testid="delete-city-planner-button"
        disabled={!canDeleteCityPlannerFolder}
      >
        <ListItemIcon>
          <Delete />
        </ListItemIcon>
        <Typography variant="inherit">Delete</Typography>
      </MenuItem>
      <StarMenuItem onMenuClose={onMenuClose} resource={cityPlannerFolder} source={source} />
      {showModal === "move" && <MoveFolderDialog onClose={closeModal} folder={cityPlannerFolder} />}
      {showModal === "edit" && <EditCityPlannerModal onClose={closeModal} cityPlannerFolder={cityPlannerFolder} />}
      {showModal === "delete" && <DeleteCityPlannerModal onClose={closeModal} cityPlannerFolder={cityPlannerFolder} />}
      {showModal === "share" && <Share resource={cityPlannerFolder} onClose={closeModal} />}
    </Menu>
  );
};

export default CityPlannerRowMenu;
